import { FC, useMemo } from 'react';

import HighchartsReact from 'highcharts-react-official';
import { useYearQuarterNavigation } from 'hooks';
import Highcharts from 'lib/highcharts';
import { useTranslation } from 'react-i18next';

import { BLUE } from 'constants/colors';

import { getLocalizedMonthNames } from 'utils/date';
import { displayRoundedNumber } from 'utils/misc';

import { BaseplanOverview } from 'RestAPIs/capacity-plan/analyzer';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';

const monthNames = getLocalizedMonthNames();

interface BaseplanChartProps {
  baseplans: BaseplanOverview[];
}

const BaseplanChart: FC<BaseplanChartProps> = ({ baseplans }) => {
  const { t } = useTranslation('common');
  const { isViewPerWeek, interval } = useYearQuarterNavigation();

  const options = useMemo(() => {
    const series = baseplans.map(({ isBasePlan, periods }) => ({
      name: isBasePlan ? t('baseplan.name') : t('baseplan.workSupply'),
      stack: t('totals'),
      color: isBasePlan ? BLUE : 'rgba(0, 148, 191, 0.6)',
      data: periods
        .slice(interval.start, interval.end || periods.length)
        .map(({ hours }) => hours)
    }));

    const options = {
      title: {
        text: null
      },
      chart: {
        type: 'column',
        numberFormatter(value) {
          return displayRoundedNumber(value);
        }
      },
      credits: {
        enabled: false
      },
      plotOptions: {
        column: {
          stacking: 'normal'
        }
      },
      xAxis: {
        categories: isViewPerWeek
          ? baseplans[0].periods
              .slice(
                interval.start,
                interval?.end || baseplans[0].periods.length
              )
              .map((p) => `${t('week')} ${p.periodNr}`)
          : monthNames,
        crosshair: true
      },
      yAxis: {
        min: 0,
        allowDecimals: true,
        title: {
          text: t('hours')
        }
      },
      tooltip: {
        pointFormat:
          '<b>{series.userOptions.stack}</b>' +
          '<br/><span style="color:{point.color}">  ●</span><span> {series.name}</span>: {point.y:,.0f}<br/>' +
          'Total: {point.stackTotal:,.0f}'
      },
      series
    };

    return options;
  }, [baseplans, isViewPerWeek, interval]);

  return (
    <StyledPaper>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </StyledPaper>
  );
};

export default BaseplanChart;
